/**
 * 🟦 V18.3 — PlanesReglasModal.
 *
 * Modal con las REGLAS OFICIALES de uso de DamyxIA y Lyon-IA
 * (Cuadro Negro Oficial — feb 2026).
 *
 * Se abre desde el rectángulo "Planes y Beneficios" en el HomepageModal.
 * Contiene el contenido textual oficial dictado por Marcelo + un botón
 * "Ver planes completos →" que sigue navegando a /planes-y-beneficios.
 *
 * NO modifica la estructura ni el diseño de /planes-y-beneficios.
 *
 * 🟦 2026-06-27 — i18n bilingüe ES/EN via `useLanguage()`.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, ArrowUp, ArrowRight, Crown, Gift, Sparkles, Lock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Sub-componentes auxiliares (fuera del render para evitar remount).
const Section = ({ icon: Icon, color, title, children }) => (
  <section className={`rounded-xl border border-${color}-500/30 bg-${color}-500/5 p-4 sm:p-5 space-y-3`}>
    <div className="flex items-center gap-2">
      <Icon className={`w-5 h-5 text-${color}-300`} />
      <h3 className={`text-base sm:text-lg font-bold text-${color}-200`}>{title}</h3>
    </div>
    <div className="text-slate-200 text-sm sm:text-[14.5px] leading-relaxed space-y-2.5">{children}</div>
  </section>
);

const Sub = ({ title, children }) => (
  <div className="ml-1">
    <p className="text-white font-semibold mt-2">{title}</p>
    <div className="pl-1 mt-1">{children}</div>
  </div>
);

const Bul = ({ children }) => (
  <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">{children}</ul>
);

const PlanesReglasModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const es = language === 'es';
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [isOpen]);

  if (!isOpen) return null;

  const scrollToTop = () => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGoFullPage = () => {
    onClose?.();
    navigate('/planes-y-beneficios');
  };

  return createPortal(
    <div
      data-testid="planes-reglas-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[10002] bg-black/75 backdrop-blur-md flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-3xl max-h-[92vh] bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 border border-purple-500/40 rounded-2xl shadow-2xl shadow-purple-500/20 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/30 bg-gradient-to-r from-purple-900/40 to-fuchsia-900/30 shrink-0">
          <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-300" />
            {es ? 'Reglas Oficiales — DamyxIA y Lyon-IA' : 'Official Rules — DamyxIA and Lyon-IA'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="planes-reglas-close"
            aria-label={es ? 'Cerrar' : 'Close'}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          ref={scrollRef}
          data-testid="planes-reglas-scroll"
          className="flex-1 overflow-y-auto px-5 sm:px-7 py-6 space-y-5"
        >
          <p className="text-xs sm:text-sm text-slate-400 italic border-l-2 border-purple-500/40 pl-3">
            {es
              ? 'Cuadro Negro Oficial — Documento único, completo y obligatorio para el uso de DamyxIA y Lyon-IA.'
              : 'Official Black Frame — Single, complete and mandatory document for the use of DamyxIA and Lyon-IA.'}
          </p>

          {/* 1. PRIMERA REGISTRACIÓN — 30 DÍAS GRATIS */}
          <Section icon={Gift} color="emerald" title={es ? '1. Primera Registración — 30 Días Gratis' : '1. First Registration — 30 Free Days'}>
            <Sub title={es ? '1.1 Imágenes (Acciones Rápidas)' : '1.1 Images (Quick Actions)'}>
              <Bul>
                <li>{es ? '12 imágenes totales durante los 30 días.' : '12 total images during the 30 days.'}</li>
                <li>{es ? 'El usuario puede usarlas en un solo día o distribuirlas en los 30 días.' : 'You may use them all in a single day or spread them across the 30 days.'}</li>
                <li>{es ? 'No incluye funciones Premium.' : 'Does not include Premium features.'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '1.2 Créditos' : '1.2 Credits'}>
              <Bul>
                <li>{es ? '100 créditos totales durante los 30 días.' : '100 total credits during the 30 days.'}</li>
              </Bul>
              <p className="mt-2">{es ? 'Sirven para:' : 'They are used for:'}</p>
              <Bul>
                <li>{es ? 'Crear imágenes' : 'Create images'}</li>
                <li>{es ? 'Modificar imágenes' : 'Modify images'}</li>
                <li>{es ? 'Corregir errores' : 'Fix errors'}</li>
                <li>{es ? 'Ejecutar órdenes dentro de DamyxIA que consuman créditos' : 'Execute commands inside DamyxIA that consume credits'}</li>
              </Bul>
              <p className="mt-2">
                {es
                  ? 'Cuando se agotan los 100 créditos: el usuario pasa a 8 créditos diarios por el tiempo que reste de los 30 días.'
                  : 'When the 100 credits are used up: the user switches to 8 daily credits for the remaining time of the 30 days.'}
              </p>
            </Sub>
            <Sub title={es ? '1.3 Conversaciones con Lyon-IA' : '1.3 Conversations with Lyon-IA'}>
              <Bul>
                <li>{es ? 'ILIMITADAS.' : 'UNLIMITED.'}</li>
                <li>{es ? 'Puede pedir ayuda, ideas, textos, explicaciones, estructura de apps, guía y soporte.' : 'You can ask for help, ideas, copy, explanations, app structure, guidance and support.'}</li>
                <li>{es ? 'Puede crear conversaciones y recibir ayuda para crear sus apps.' : 'You can create conversations and receive help to build your apps.'}</li>
                <li>{es ? 'Las conversaciones NO consumen créditos.' : 'Conversations do NOT consume credits.'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '1.4 Restricciones durante los 30 días' : '1.4 Restrictions during the 30 days'}>
              <Bul>
                <li>{es ? 'Sin funciones Premium' : 'No Premium features'}</li>
                <li>{es ? 'Sin botones avanzados del Genio' : 'No advanced Genie buttons'}</li>
                <li>{es ? 'Sin recetas Premium' : 'No Premium recipes'}</li>
                <li>{es ? 'Sin acciones automáticas del Builder' : 'No automatic Builder actions'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '1.5 Fin de los 30 días o 100 créditos' : '1.5 End of the 30 days or 100 credits'}>
              <Bul>
                <li>{es ? 'Al terminar, el usuario pasa AUTOMÁTICAMENTE al Plan Gratis.' : 'Upon completion, the user AUTOMATICALLY transitions to the Free Plan.'}</li>
                <li>{es ? 'No requiere ninguna acción del usuario.' : 'No action from the user is required.'}</li>
              </Bul>
            </Sub>
          </Section>

          {/* 2. PLAN GRATIS */}
          <Section icon={Lock} color="slate" title={es ? '2. Plan Gratis (después de terminar los 30 días o 100 créditos)' : '2. Free Plan (after finishing the 30 days or 100 credits)'}>
            <Sub title={es ? '2.1 Imágenes (Acciones Rápidas)' : '2.1 Images (Quick Actions)'}>
              <Bul>
                <li>{es ? '8 imágenes por mes.' : '8 images per month.'}</li>
                <li>{es ? 'Solo en Acciones Rápidas.' : 'Only in Quick Actions.'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '2.2 Créditos diarios' : '2.2 Daily credits'}>
              <Bul>
                <li>{es ? '8 créditos por día.' : '8 credits per day.'}</li>
                <li>{es ? 'Para órdenes de creación o modificación de imágenes dentro de DamyxIA.' : 'For image creation or modification commands inside DamyxIA.'}</li>
                <li>{es ? 'No acumulables (se resetean cada día).' : 'Non-accumulative (reset every day).'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '2.3 Conversaciones con Lyon-IA' : '2.3 Conversations with Lyon-IA'}>
              <Bul>
                <li>{es ? 'ILIMITADAS.' : 'UNLIMITED.'}</li>
                <li>{es ? 'Puede crear conversaciones y recibir ayuda para crear sus apps.' : 'You can create conversations and receive help to build your apps.'}</li>
                <li>{es ? 'Lyon-IA puede explicar, guiar, sugerir, redactar, corregir y acompañar.' : 'Lyon-IA can explain, guide, suggest, write, correct and accompany you.'}</li>
                <li>{es ? 'Las conversaciones NO consumen créditos.' : 'Conversations do NOT consume credits.'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '2.4 Restricciones del Plan Gratis' : '2.4 Free Plan restrictions'}>
              <Bul>
                <li>{es ? 'Sin funciones Premium' : 'No Premium features'}</li>
                <li>{es ? 'Sin botones avanzados del Genio' : 'No advanced Genie buttons'}</li>
                <li>{es ? 'Sin recetas Premium' : 'No Premium recipes'}</li>
                <li>{es ? 'Sin acciones automáticas del Builder' : 'No automatic Builder actions'}</li>
              </Bul>
            </Sub>
          </Section>

          {/* 3. PLAN PREMIUM */}
          <Section icon={Crown} color="amber" title={es ? '3. Plan Premium — $9 USD/mes o $86 USD/año' : '3. Premium Plan — $9 USD/month or $86 USD/year'}>
            <Sub title={es ? '3.1 Conversaciones con Lyon-IA' : '3.1 Conversations with Lyon-IA'}>
              <Bul>
                <li>{es ? 'ILIMITADAS.' : 'UNLIMITED.'}</li>
                <li>{es ? 'Acceso completo a todas las capacidades del Genio.' : 'Full access to all Genie capabilities.'}</li>
                <li>{es ? 'Puede usar botones avanzados, recetas, acciones automáticas y funciones Premium.' : 'Can use advanced buttons, recipes, automatic actions and Premium features.'}</li>
                <li>{es ? 'Uso total de Acciones Rápidas sin límite.' : 'Full unlimited use of Quick Actions.'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '3.2 Funciones Premium habilitadas' : '3.2 Enabled Premium features'}>
              <Bul>
                <li>{es ? 'Botones avanzados del Genio' : 'Advanced Genie buttons'}</li>
                <li>{es ? 'Recetas Premium' : 'Premium recipes'}</li>
                <li>{es ? 'Acciones automáticas del Builder' : 'Automatic Builder actions'}</li>
                <li>{es ? 'Modificaciones avanzadas' : 'Advanced modifications'}</li>
                <li>{es ? 'Aplicación de estilos, layouts y automatizaciones' : 'Application of styles, layouts and automations'}</li>
                <li>{es ? 'Importación de recetas IA' : 'AI recipe imports'}</li>
                <li>{es ? 'Uso completo del sistema sin ninguna restricción' : 'Full system use without any restriction'}</li>
              </Bul>
            </Sub>
          </Section>

          {/* 4. FUNCIONAMIENTO GENERAL DE LYON-IA */}
          <Section icon={Sparkles} color="fuchsia" title={es ? '4. Funcionamiento general de Lyon-IA' : '4. General Lyon-IA behavior'}>
            <Sub title={es ? '4.1 Disponible para todos los usuarios' : '4.1 Available to all users'}>
              <Bul>
                <li>{es ? 'Conversaciones ilimitadas siempre.' : 'Unlimited conversations always.'}</li>
                <li>{es ? 'En todos los planes sin excepción.' : 'In every plan without exception.'}</li>
                <li>{es ? 'Puede crear conversaciones y recibir ayuda para crear sus apps.' : 'You can create conversations and receive help to build your apps.'}</li>
                <li>{es ? 'Lyon-IA puede explicar, sugerir, redactar, corregir, estructurar y guiar.' : 'Lyon-IA can explain, suggest, write, correct, structure and guide.'}</li>
              </Bul>
            </Sub>
            <Sub title={es ? '4.2 Diferencias por plan' : '4.2 Differences per plan'}>
              <p className="text-emerald-200 font-semibold mt-2">{es ? '30 Días Gratis y Plan Gratis' : '30 Free Days and Free Plan'}</p>
              <Bul>
                <li>{es ? 'Conversaciones ilimitadas con Lyon-IA ✅' : 'Unlimited conversations with Lyon-IA ✅'}</li>
                <li>{es ? 'Ayuda ilimitada ✅' : 'Unlimited help ✅'}</li>
                <li>{es ? 'Guía para crear apps ✅' : 'Guidance to build apps ✅'}</li>
                <li>{es ? 'Sin funciones Premium ❌' : 'No Premium features ❌'}</li>
                <li>{es ? 'Sin botones avanzados ❌' : 'No advanced buttons ❌'}</li>
                <li>{es ? 'Sin recetas Premium ❌' : 'No Premium recipes ❌'}</li>
                <li>{es ? 'Sin acciones automáticas del Builder ❌' : 'No automatic Builder actions ❌'}</li>
              </Bul>
              <p className="text-amber-200 font-semibold mt-3">{es ? 'Plan Premium' : 'Premium Plan'}</p>
              <Bul>
                <li>{es ? 'Conversaciones ilimitadas con Lyon-IA ✅' : 'Unlimited conversations with Lyon-IA ✅'}</li>
                <li>{es ? 'Acceso total a funciones Premium ✅' : 'Full access to Premium features ✅'}</li>
                <li>{es ? 'Botones avanzados del Genio ✅' : 'Advanced Genie buttons ✅'}</li>
                <li>{es ? 'Recetas Premium ✅' : 'Premium recipes ✅'}</li>
                <li>{es ? 'Acciones automáticas del Builder ✅' : 'Automatic Builder actions ✅'}</li>
                <li>{es ? 'Modificaciones avanzadas ✅' : 'Advanced modifications ✅'}</li>
                <li>{es ? 'Estilos, layouts y automatizaciones ✅' : 'Styles, layouts and automations ✅'}</li>
              </Bul>
            </Sub>
          </Section>

          {/* Fecha del documento */}
          <p className="text-right text-xs text-slate-500 pt-1" data-testid="planes-reglas-date">
            {es ? '05/07/2026' : '07/05/2026'}
          </p>

          {/* Acciones */}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={scrollToTop}
              data-testid="planes-reglas-back-to-top"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white text-sm font-semibold border border-slate-700/60 transition-colors"
            >
              <ArrowUp className="w-4 h-4" />
              {es ? 'Volver al principio' : 'Back to top'}
            </button>
            <button
              type="button"
              onClick={handleGoFullPage}
              data-testid="planes-reglas-full-page"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white text-sm font-semibold shadow-lg shadow-purple-500/30 transition-all hover:scale-105"
            >
              {es ? 'Ver planes completos' : 'View full plans'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PlanesReglasModal;
